import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import useForm from 'shared/hooks/useForm';
import { LANGUAGES } from 'shared/constants';
import { isEmailValid, isPhoneValid } from 'shared/lib/validators';
import { Input, ButtonsRow } from 'shared/components/Form';
import CloseButton from 'shared/components/buttons/Close';
import { addSuccessNotification, addErrorNotification } from 'shared/lib/notifications';
import styles from './FeedbackWidget.module.scss';

// useForm prevadi ciselne retezce u ne-textovych inputu na number, proto vzdy pres String
const toText = value => (value === null || value === undefined ? '' : String(value)).trim();

const FeedbackWidget = ({ apiClient, userEmail }) => {
  const { t, i18n } = useTranslation('common');
  const [opened, setOpened] = useState(false);
  const [processing, setProcessing] = useState(false);

  const close = () => setOpened(false);

  const onSubmit = async (data) => {
    setProcessing(true);
    try {
      await apiClient.post('/feedback', {
        message: toText(data.message),
        ...(toText(data.email) ? { email: toText(data.email) } : {}),
        ...(toText(data.phone) ? { phone: toText(data.phone) } : {}),
        website: data.website,
        pageUrl: window.location.pathname + window.location.search,
        ...(LANGUAGES.includes(i18n.language) ? { language: i18n.language } : {}),
      });
      addSuccessNotification(t('feedback.thanks'));
      handleReset();
      close();
    } catch {
      addErrorNotification(t('error.common'));
    }
    setProcessing(false);
  };

  const validate = (data) => {
    const errs = {};
    if (!toText(data.message)) {
      errs.message = t('feedback.fillMessage');
    }
    if (toText(data.email) && !isEmailValid(toText(data.email))) {
      errs.email = t('validation.fillValidEmail');
    }
    // stejne jako ostatni formulare: validuje se jen "+" a cislice, mezery a pomlcky se ignoruji
    if (toText(data.phone) && !isPhoneValid(toText(data.phone).replace(/[^+0-9]+/g, ''))) {
      errs.phone = t('feedback.fillValidPhone');
    }
    return errs;
  };

  const {
    values, errors, setValues, handleChange, handleSubmit, handleReset
  } = useForm(onSubmit, {
    message: '', email: userEmail || '', phone: '', website: ''
  }, validate, { scrollToError: false, scrollToTop: false });

  const open = () => {
    if (!values.email && userEmail) {
      setValues(prev => ({ ...prev, email: userEmail }));
    }
    setOpened(true);
  };

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={open}
        aria-label={t('feedback.button')}
        title={t('feedback.button')}
      >
        <i className="la la-comment-dots" />
      </button>
      <Modal isOpen={opened} toggle={close} centered returnFocusAfterClose>
        <ModalHeader close={<CloseButton onClick={close} />}>
          {t('feedback.title')}
        </ModalHeader>
        <ModalBody>
          <p className={styles.intro}>{t('feedback.intro')}</p>
          <form onSubmit={handleSubmit}>
            <Input
              multiline
              rows={5}
              iconClass="la la-sticky-note"
              name="message"
              value={values.message}
              onChange={handleChange}
              label={t('feedback.message')}
              error={errors.message}
              maxLength={5000}
            />
            <Input
              type="email"
              iconClass="la la-at"
              name="email"
              value={values.email}
              onChange={handleChange}
              label={t('feedback.email')}
              error={errors.email}
              hint={<small className={styles.hint}>{t('feedback.emailHint')}</small>}
            />
            <Input
              inputMode="tel"
              iconClass="la la-phone"
              name="phone"
              value={values.phone}
              onChange={handleChange}
              label={t('feedback.phone')}
              error={errors.phone}
            />
            {/* honeypot: skryte pole vyplni jen bot, server takove odeslani tise zahodi */}
            <div className={styles.honeypot} aria-hidden="true">
              <input
                type="text"
                name="website"
                value={values.website}
                onChange={handleChange}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <ButtonsRow
              processing={processing}
              onSubmit={handleSubmit}
              onCancel={close}
              submitTitle={t('button.send')}
              cancelTitle={t('button.close')}
              noMargin
            />
          </form>
        </ModalBody>
      </Modal>
    </>
  );
};

FeedbackWidget.propTypes = {
  apiClient: PropTypes.shape({
    post: PropTypes.func.isRequired,
  }).isRequired,
  userEmail: PropTypes.string,
};

FeedbackWidget.defaultProps = {
  userEmail: null,
};

export default FeedbackWidget;
